import { useFetchedSurvey } from './useFetchedSurvey';
import { useMemoCompare } from '../../hooks/useMemoCompare';
import { useAppSelector } from '../../app/hooks';
import _ from 'lodash';
import { BusinessPlanSurvey } from '../types';
import { useMemo } from 'react';
import { useUpdateSurveyMutation } from './useUpdateSurveyMutation';
import { useMutation } from '@tanstack/react-query';

export const useSurveyWithLocalChanges = () => {
  const { query } = useFetchedSurvey();
  const localChanges = useAppSelector((state) => state.businessPlan.sections) ?? [];
  const localChangesMemoized = useMemoCompare(localChanges, (a, b) => _.isEqual(a, b));

  const surveyWithLocalChanges: BusinessPlanSurvey = useMemo(
    () =>
      query.data
        ? {
            sections:
              query.data.sections.map((x) => ({
                ...x,
                value: localChangesMemoized[x.key] ?? x.value,
              })) ?? [],
            version: query.data.version,
          }
        : { sections: [], version: null },
    [query.data, localChangesMemoized]
  );

  const hasUnsavedChanges = query.data
    ? query.data.sections.some(
        (x) => localChanges[x.key] !== undefined && x.value !== localChanges[x.key]
      )
    : false;

  const { mutateAsync: updateSurvey } = useUpdateSurveyMutation();
  const saveSurveyMutation = useMutation({
    mutationFn: async () => {
      if (!query.data) return;
      if (!hasUnsavedChanges) return;
      await updateSurvey(surveyWithLocalChanges);
    },
  });

  return {
    query: { ...query, data: surveyWithLocalChanges },
    hasUnsavedChanges,
    saveSurveyMutation,
  };
};
